import React, { useEffect, useState, useCallback } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { hasAccess } from '../utils/permissions';
import { API_BASE_URL } from '../utils/config';
import {
    PageHeader, PageShell, Surface, MetricCard, StatePanel, Empty,
} from '../components/ui';

const RANGES = [7, 30, 90];

const PLATFORMS = [
    { value: '', label: 'All' },
    { value: 'web', label: 'Web' },
    { value: 'mobile', label: 'Mobile' },
];

const PLATFORM_LABELS = { web: 'Web', mobile: 'Mobile' };

export const PLACEHOLDER = '—';

const fmtInt = n => Number(n || 0).toLocaleString('en-US');

const fmtDate = iso => {
    if (!iso) return PLACEHOLDER;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return PLACEHOLDER;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// Every field added by the platform/device backend change may be absent when
// that backend is not deployed yet, so cells go through this rather than
// rendering the raw value (which would print "undefined" or crash on objects).
export const display = value => {
    if (value === undefined || value === null) return PLACEHOLDER;
    const text = String(value).trim();
    return text ? text : PLACEHOLDER;
};

// `screen` is reported by the beacon; accept a preformatted string, a number,
// or a { width, height } object without assuming which one the backend sends.
export const formatScreen = screen => {
    if (screen === undefined || screen === null || screen === '') return PLACEHOLDER;
    if (typeof screen === 'object') {
        const w = screen.width !== undefined ? screen.width : screen.w;
        const h = screen.height !== undefined ? screen.height : screen.h;
        return w && h ? `${w}×${h}` : PLACEHOLDER;
    }
    return display(screen);
};

// Best-effort browser + OS label for web visitors, whose rows carry only a
// user-agent string. Plain substring matching; order matters because most
// browsers also claim to be the ones they were forked from (Edge says Chrome,
// Chrome says Safari, Android says Linux, iPadOS can say Macintosh).
export const describeUserAgent = ua => {
    if (!ua || typeof ua !== 'string') return { browser: null, os: null, label: null };
    let browser = null;
    if (/Edg(e|A|iOS)?\//.test(ua)) browser = 'Edge';
    else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
    else if (/Firefox\/|FxiOS\//.test(ua)) browser = 'Firefox';
    else if (/Chrome\/|CriOS\/|Chromium\//.test(ua)) browser = 'Chrome';
    else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = 'Safari';

    let os = null;
    if (/iPhone|iPad|iPod|iOS/.test(ua)) os = 'iOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
    else if (/CrOS/.test(ua)) os = 'ChromeOS';
    else if (/Linux/.test(ua)) os = 'Linux';

    const label = [browser, os].filter(Boolean).join(' · ') || null;
    return { browser, os, label };
};

// Device cell: real os/osVersion when the beacon sent them (mobile), else a
// label derived from the user agent (web). The raw UA lives in the tooltip.
export const deviceLabel = row => {
    if (row && row.os) return { text: `${row.os}${row.osVersion ? ` ${row.osVersion}` : ''}`, derived: false };
    const { label } = describeUserAgent(row && row.userAgent);
    return { text: label || PLACEHOLDER, derived: Boolean(label) };
};

const PlatformPill = ({ platform }) => {
    const label = PLATFORM_LABELS[platform];
    if (!label) return <span className="sk-muted">{PLACEHOLDER}</span>;
    return <span className={`sk-pill sk-platform-pill is-${platform}`}>{label}</span>;
};

const TruncCell = ({ value, className = '' }) => {
    const text = display(value);
    return (
        <td className={`sk-cell-truncate ${className}`.trim()} title={text === PLACEHOLDER ? undefined : text}>
            {text}
        </td>
    );
};

export default function GuestVisitors() {
    const { permissions, loaded } = usePermissions();
    const roles = (permissions && permissions.adminRoles) || [];
    const allowed = loaded && hasAccess('guest-visitors', roles);
    const [days, setDays] = useState(30);
    const [platform, setPlatform] = useState('');
    const [page, setPage] = useState(1);
    const [countryFilter, setCountryFilter] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleCountryChange = useCallback((e) => {
        const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
        setCountryFilter(val);
        if (val.length === 2 || val.length === 0) {
            setPage(1);
        }
    }, []);

    const handlePlatformChange = useCallback((value) => {
        setPlatform(value);
        setPage(1);
    }, []);

    useEffect(() => {
        if (!allowed) return undefined;
        // Only fetch when country filter is empty or exactly 2 letters
        if (countryFilter.length === 1) return undefined;
        let alive = true;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ days, page, limit: 25 });
        if (countryFilter.length === 2) params.set('country', countryFilter);
        if (platform) params.set('platform', platform);
        httpClient(`${API_BASE_URL}/admin/guest-visitors?${params}`, { method: 'GET', cache: 'no-store' })
            .then(({ json }) => { if (alive) setData(json); })
            .catch(e => { if (alive) setError(e && e.status === 403 ? 'You do not have access to this page.' : 'Could not load guest visitors.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, page, countryFilter, platform, allowed]);

    // Reset page when days changes
    useEffect(() => { setPage(1); }, [days]);

    if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
    if (!hasAccess('guest-visitors', roles)) return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;

    const headerActions = (
        <div className="sk-guest-filters">
            <div className="sk-dashboard-chart-tabs" role="group" aria-label="Platform">
                {PLATFORMS.map(p => (
                    <button key={p.label} type="button" aria-pressed={platform === p.value} className={platform === p.value ? 'active' : ''} onClick={() => handlePlatformChange(p.value)}>{p.label}</button>
                ))}
            </div>
            <div className="sk-dashboard-chart-tabs" role="group" aria-label="Date range">
                {RANGES.map(d => (
                    <button key={d} type="button" aria-pressed={days === d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d} days</button>
                ))}
            </div>
        </div>
    );

    const totalPages = (data && data.totalPages_pagination) || 1;
    const rows = (data && Array.isArray(data.rows)) ? data.rows : [];
    const platformCounts = (data && Array.isArray(data.platformBreakdown))
        ? data.platformBreakdown.reduce((acc, p) => ({ ...acc, [p.platform]: p.count }), {})
        : null;

    return (
        <main className="sk-page">
            <PageHeader eyebrow="Insights" title="Guest Visitors"
                description="Anonymous visitors who have not signed in, on web and on mobile builds that send the guest beacon."
                actions={headerActions} />

            {loading && <StatePanel state="loading">Loading guest visitors…</StatePanel>}
            {error && <StatePanel state="error">{error}</StatePanel>}

            {data && (
                <>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <MetricCard tone="purple" label="Unique guests" value={fmtInt(data.totalGuests)} detail={`Last ${days} days`} />
                        <MetricCard tone="blue" label="Total visits" value={fmtInt(data.totalVisits)} detail={`Page views in window`} />
                        {platformCounts && (
                            <>
                                <MetricCard tone="green" label="Web guests" value={fmtInt(platformCounts.web)} detail="Platform: web" />
                                <MetricCard tone="orange" label="Mobile guests" value={fmtInt(platformCounts.mobile)} detail="Platform: mobile app" />
                            </>
                        )}
                    </div>

                    {/* Per-country breakdown */}
                    {data.countryBreakdown && data.countryBreakdown.length > 0 && (
                        <Surface title="Visitors by country" description={`Last ${days} days — top countries`}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                {data.countryBreakdown.map(c => (
                                    <span key={c.country} className="sk-pill"
                                        style={{
                                            cursor: 'pointer',
                                            background: countryFilter === c.country ? 'var(--sk-metric-purple)' : 'var(--sk-canvas)',
                                            color: countryFilter === c.country ? 'var(--sk-metric-purple-fg)' : 'var(--sk-ink)',
                                            border: '1px solid var(--sk-border)',
                                            padding: '4px 10px',
                                            fontSize: 12,
                                            fontWeight: 700
                                        }}
                                        onClick={() => {
                                            setCountryFilter(prev => prev === c.country ? '' : c.country);
                                            setPage(1);
                                        }}>
                                        {c.country}: {fmtInt(c.count)}
                                    </span>
                                ))}
                            </div>
                        </Surface>
                    )}

                    <Surface title="Guest visitor list" description={`Last ${days} days`}
                        actions={
                            <input
                                type="text"
                                value={countryFilter}
                                onChange={handleCountryChange}
                                placeholder="Country (e.g. LB)"
                                aria-label="Country code filter"
                                style={{
                                    padding: '6px 10px',
                                    borderRadius: 8,
                                    border: '1px solid #ccc',
                                    width: 120,
                                    fontSize: 13
                                }}
                            />
                        }>

                        <p className="sk-muted" style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5 }}>
                            <strong>Note:</strong> Guest tracking covers the <strong>web app</strong> plus <strong>mobile app builds that ship the guest beacon</strong>; older mobile builds send nothing. It counts visits since tracking was deployed — there is no backfill, and visits recorded before platform tracking existed report platform <strong>web</strong>. Country is derived from the visitor's network location via Cloudflare and may be missing or wrong behind a VPN. The visitor ID is generated on the device, so one person using several browsers or devices, or clearing their storage, counts as several visitors. No IP address is stored.
                        </p>

                        {rows.length === 0 && <Empty>No guest visitors in this window.</Empty>}

                        {rows.length > 0 && (
                            <div className="sk-table-wrap">
                                <table className="sk-data-table sk-guest-table">
                                    <thead>
                                        <tr>
                                            <th>Visitor</th>
                                            <th>Platform</th>
                                            <th>Country</th>
                                            <th>Device</th>
                                            <th>Model</th>
                                            <th>App version</th>
                                            <th>Screen</th>
                                            <th>Timezone</th>
                                            <th>Language</th>
                                            <th>Path</th>
                                            <th>Referrer</th>
                                            <th className="sk-num">Visits</th>
                                            <th>First seen</th>
                                            <th>Last seen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => {
                                            const id = row.visitorId ? String(row.visitorId) : '';
                                            const device = deviceLabel(row);
                                            const deviceTitle = row.userAgent
                                                ? (device.derived ? `Derived from user agent: ${row.userAgent}` : row.userAgent)
                                                : undefined;
                                            return (
                                                <tr key={id || `row-${i}`} data-testid="guest-row">
                                                    <td title={id || undefined}><code>{id ? `${id.slice(0, 12)}${id.length > 12 ? '…' : ''}` : PLACEHOLDER}</code></td>
                                                    <td><PlatformPill platform={row.platform} /></td>
                                                    <td>{row.country ? row.country : <span className="sk-muted">Unknown</span>}</td>
                                                    <td className="sk-cell-truncate" title={deviceTitle}>
                                                        {device.derived ? <span className="sk-muted">{device.text}</span> : device.text}
                                                    </td>
                                                    <TruncCell value={row.deviceModel} />
                                                    <td>{display(row.appVersion)}</td>
                                                    <td>{formatScreen(row.screen)}</td>
                                                    <TruncCell value={row.timezone} />
                                                    <td>{display(row.language)}</td>
                                                    <TruncCell value={row.path} />
                                                    <TruncCell value={row.referrer} className="is-wide" />
                                                    <td className="sk-num">{fmtInt(row.visitCount)}</td>
                                                    <td>{fmtDate(row.firstSeenAt)}</td>
                                                    <td>{fmtDate(row.lastSeenAt)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--sk-border)', background: 'var(--sk-surface)', cursor: page <= 1 ? 'default' : 'pointer' }}>
                                    Previous
                                </button>
                                <span style={{ padding: '6px 14px', fontSize: 13 }}>Page {page} of {totalPages}</span>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--sk-border)', background: 'var(--sk-surface)', cursor: page >= totalPages ? 'default' : 'pointer' }}>
                                    Next
                                </button>
                            </div>
                        )}
                    </Surface>
                </>
            )}
        </main>
    );
}
